import React, { createContext, useContext, useState, useEffect } from "react"
import * as WorkspaceAPI from "trimble-connect-workspace-api"

interface AppContextType {
  API?: WorkspaceAPI.WorkspaceAPI
  accessToken: string
  projectID: string
  projectLocation: string
  loadedModels: WorkspaceAPI.ModelSpec[]
  selectedModels: WorkspaceAPI.ModelSpec[]
  setLoadedModels: React.Dispatch<React.SetStateAction<WorkspaceAPI.ModelSpec[]>>
}

export const AppContext = createContext<AppContextType | undefined>(undefined)

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [API, setAPI] = useState<WorkspaceAPI.WorkspaceAPI | undefined>(undefined)
  const [accessToken, setAccessToken] = useState("")
  const [projectID, setProjectID] = useState("")
  const [projectLocation, setProjectLocation] = useState<string>("")
  const [loadedModels, setLoadedModels] = useState<WorkspaceAPI.ModelSpec[]>([])
  const [selectedModels, setSelectedModels] = useState<WorkspaceAPI.ModelSpec[]>([])

  useEffect(() => {
    const getAPI = async () => {
      const tempAPI = await WorkspaceAPI.connect(window.parent, async (event: any, data: any) => {
        //console.log("3dView Extension Received event: ", event, data)
        switch (event) {
          case "extension.accessToken": {
            setAccessToken(data.access_token)
            break
          }
          case "viewer.onModelStateChanged": {
            try {
              const loadedModels = await tempAPI.viewer.getModels("loaded")
              setLoadedModels(loadedModels)
            } catch {
              console.log("Error in viewer.onModelStateChanged")
            }
            break
          }
          case "viewer.onSelectionChanged": {
            try {
              setSelectedModels(data.data)
            } catch {
              console.log("Error in viewer.onSelectionChanged")
            }
            break
          }
        }
      })
      const access_token = await tempAPI.extension.requestPermission("accesstoken")
      const project_details = await tempAPI.project.getProject()
      const loadedModels = await tempAPI.viewer.getModels("loaded")
      const selectedModels: WorkspaceAPI.ModelSpec[] = []
      setAPI(tempAPI)
      setAccessToken(access_token)
      setProjectID(project_details.id)
      setProjectLocation(project_details.location || "europe")
      setLoadedModels(loadedModels)
      setSelectedModels(selectedModels)
    }
    getAPI()
  }, [])

  return (
    <AppContext.Provider
      value={{
        API,
        accessToken,
        projectID,
        projectLocation,
        loadedModels,
        selectedModels,
        setLoadedModels
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider")
  }
  return context
}
